/**
 * Beyza Fashion — lightweight local wishlist (no app, no account needed).
 *
 * Storage: localStorage key "beyza_wishlist" holds a JSON array of
 *   { id, handle, url, title, image, price, compare, variantId, addedAt }.
 * If storage is blocked (private mode, disabled cookies) the wishlist keeps working
 * for the current page view and never throws.
 *
 * Public API (window.BeyzaWishlist):
 *   items(), count(), has(handle), add(item), remove(handle), toggle(detail), paint(root)
 * The product card script (assets/beyza-product-card.js) calls toggle(detail) and paint(root).
 * Events: "beyza:wishlist:updated" on document, detail { items, count }.
 */
(function () {
  'use strict';
  if (window.BeyzaWishlist && window.BeyzaWishlist.__beyza) return;

  var KEY = 'beyza_wishlist';
  var LEGACY_KEY = 'beyza:wishlist';
  var MAX_ITEMS = 100;
  var memory = null;
  var cfg = window.BeyzaWishlistConfig || {};
  var ROOT = typeof cfg.root === 'string' && cfg.root ? cfg.root.replace(/\/$/, '') : '';

  /* ---------------- Storage ---------------- */
  function storage() {
    try {
      var s = window.localStorage;
      var probe = '__beyza_wl_probe__';
      s.setItem(probe, '1');
      s.removeItem(probe);
      return s;
    } catch (e) {
      return null;
    }
  }
  var store = storage();

  function str(v, max) {
    if (v === null || v === undefined) return '';
    var out = String(v);
    return max && out.length > max ? out.slice(0, max) : out;
  }

  function safeUrl(u) {
    var v = str(u, 600).trim();
    if (!v) return '';
    if (/^\/(?!\/)/.test(v)) return v;
    if (/^(https?:)?\/\//i.test(v)) return v;
    return '';
  }

  function clean(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var handle = str(raw.handle, 255).trim();
    if (!handle || !/^[\w\-À-￿%.]+$/.test(handle)) return null;
    return {
      id: str(raw.id, 40),
      handle: handle,
      url: safeUrl(raw.url) || ROOT + '/products/' + encodeURIComponent(handle),
      title: str(raw.title, 300),
      image: safeUrl(raw.image),
      price: str(raw.price, 60),
      compare: str(raw.compare, 60),
      variantId: str(raw.variantId, 40),
      addedAt: Number(raw.addedAt) || Date.now()
    };
  }

  function parse(text) {
    try {
      var data = JSON.parse(text || '[]');
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  }

  function read() {
    if (!store) return (memory || []).slice();
    var raw = null;
    try { raw = store.getItem(KEY); } catch (e) { raw = null; }
    var list;
    if (raw === null) {
      // One-time migration from the earlier handle-only key.
      var legacy = [];
      try { legacy = parse(store.getItem(LEGACY_KEY)); } catch (e) { legacy = []; }
      list = legacy.filter(function (h) { return typeof h === 'string'; }).map(function (h) { return { handle: h }; });
    } else {
      list = parse(raw);
    }
    var seen = {};
    var out = [];
    list.forEach(function (entry) {
      var item = clean(entry);
      if (item && !seen[item.handle]) {
        seen[item.handle] = true;
        out.push(item);
      }
    });
    return out;
  }

  function write(list) {
    memory = list.slice(0, MAX_ITEMS);
    if (!store) return false;
    try {
      store.setItem(KEY, JSON.stringify(memory));
      return true;
    } catch (e) {
      return false;
    }
  }

  /* ---------------- API ---------------- */
  function items() { return read(); }
  function count() { return read().length; }
  function has(handle) {
    return read().some(function (i) { return i.handle === handle; });
  }
  function add(raw) {
    var item = clean(raw);
    if (!item) return false;
    var list = read().filter(function (i) { return i.handle !== item.handle; });
    list.unshift(item);
    write(list);
    changed();
    return true;
  }
  function remove(handle) {
    var list = read();
    var next = list.filter(function (i) { return i.handle !== handle; });
    if (next.length !== list.length) {
      write(next);
      changed();
    }
  }
  function itemFromButton(btn) {
    var d = btn ? btn.dataset : {};
    return {
      id: d.productId,
      handle: d.productHandle,
      url: d.productUrl,
      title: d.productTitle,
      image: d.productImage,
      price: d.productPrice,
      compare: d.productCompare,
      variantId: d.variantId,
      addedAt: Date.now()
    };
  }
  /** Called by the product card script: detail = { handle, productId, active, button }. */
  function toggle(detail) {
    if (!detail || !detail.handle) return;
    if (detail.active) add(itemFromButton(detail.button) || { handle: detail.handle });
    else remove(detail.handle);
    bump(detail.handle);
  }

  /* ---------------- UI painting ---------------- */
  function paint(root) {
    var list = read();
    var saved = {};
    list.forEach(function (i) { saved[i.handle] = true; });
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-beyza-wishlist]').forEach(function (btn) {
      var on = !!saved[btn.dataset.productHandle];
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      var label = on ? btn.dataset.labelRemove : btn.dataset.labelAdd;
      var textEl = btn.querySelector('[data-beyza-wishlist-label]');
      if (label) {
        if (textEl) textEl.textContent = label;
        else btn.setAttribute('aria-label', label);
      }
    });
    document.querySelectorAll('[data-beyza-wishlist-count]').forEach(function (el) {
      el.textContent = list.length > 99 ? '99+' : String(list.length);
      el.hidden = list.length === 0;
    });
    document.querySelectorAll('[data-beyza-wishlist-link]').forEach(function (a) {
      a.setAttribute('aria-label', list.length ? 'Wishlist (' + list.length + ' ' + (list.length === 1 ? 'item' : 'items') + ')' : 'Wishlist');
    });
  }

  function bump(handle) {
    if (!handle || !window.CSS || !CSS.escape) return;
    document.querySelectorAll('[data-beyza-wishlist][data-product-handle="' + CSS.escape(handle) + '"], [data-beyza-wishlist-count]').forEach(function (el) {
      el.classList.remove('is-bump');
      void el.offsetWidth;
      el.classList.add('is-bump');
      setTimeout(function () { el.classList.remove('is-bump'); }, 450);
    });
  }

  var queued = false;
  function changed() {
    if (queued) return;
    queued = true;
    var run = function () {
      queued = false;
      paint(document);
      var list = read();
      document.dispatchEvent(new CustomEvent('beyza:wishlist:updated', { detail: { items: list, count: list.length } }));
      renderPages();
    };
    if (window.queueMicrotask) window.queueMicrotask(run); else setTimeout(run, 0);
  }

  /* ---------------- Wishlist page ---------------- */
  var productCache = {};

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text !== undefined && text !== null) n.textContent = text;
    return n;
  }

  function money(cents) {
    if (window.BeyzaCart && typeof window.BeyzaCart.formatMoney === 'function') {
      try { return window.BeyzaCart.formatMoney(cents); } catch (e) { /* fall through */ }
    }
    try {
      return new Intl.NumberFormat(cfg.locale || undefined, { style: 'currency', currency: cfg.currency || 'INR' }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function fetchProduct(item) {
    if (productCache[item.handle]) return productCache[item.handle];
    var url = ROOT + '/products/' + encodeURIComponent(item.handle) + '.js';
    productCache[item.handle] = fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        if (r.status === 404) return { missing: true };
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .catch(function () { delete productCache[item.handle]; return null; });
    return productCache[item.handle];
  }

  function addToCart(variantId, btn) {
    if (window.BeyzaCart && typeof window.BeyzaCart.add === 'function') {
      return window.BeyzaCart.add(variantId, 1, btn, {});
    }
    var body = new FormData();
    body.append('id', String(variantId));
    body.append('quantity', '1');
    return fetch(ROOT + '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (data) {
        if (!r.ok || data.status) throw new Error(data.description || data.message || 'Error');
        window.location.href = ROOT + '/cart';
        return data;
      });
    });
  }

  function buildItem(page, item) {
    var strings = page.dataset;
    var li = el('li', 'beyza-wishlist__item');
    li.dataset.handle = item.handle;

    var media = el('a', 'beyza-wishlist__media');
    media.href = item.url;
    media.setAttribute('tabindex', '-1');
    media.setAttribute('aria-hidden', 'true');
    var img = el('img', 'beyza-wishlist__image');
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    if (item.image) img.src = item.image; else img.hidden = true;
    media.appendChild(img);

    var removeIcon = el('button', 'beyza-wishlist__remove-icon');
    removeIcon.type = 'button';
    removeIcon.setAttribute('data-beyza-wishlist-remove', item.handle);
    removeIcon.setAttribute('aria-label', (strings.labelRemove || 'Remove from wishlist') + ': ' + (item.title || item.handle));
    removeIcon.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20.3s-7.6-4.6-9.3-9.3C1.6 7.8 3.6 4.6 6.9 4.6c2 0 3.5 1.1 5.1 3 1.6-1.9 3.1-3 5.1-3 3.3 0 5.3 3.2 4.2 6.4-1.7 4.7-9.3 9.3-9.3 9.3Z" fill="currentColor" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>';

    var mediaWrap = el('div', 'beyza-wishlist__media-wrap');
    mediaWrap.append(media, removeIcon);

    var info = el('div', 'beyza-wishlist__info');
    var title = el('h3', 'beyza-wishlist__title');
    var titleLink = el('a', 'beyza-wishlist__title-link', item.title || item.handle.replace(/-/g, ' '));
    titleLink.href = item.url;
    title.appendChild(titleLink);

    var price = el('p', 'beyza-wishlist__price');
    var current = el('span', 'beyza-wishlist__price-current' + (item.compare ? ' is-sale' : ''), item.price);
    price.appendChild(current);
    if (item.compare) price.appendChild(el('s', 'beyza-wishlist__price-compare', item.compare));

    var note = el('p', 'beyza-wishlist__note');
    note.hidden = true;

    var add = el('button', 'beyza-wishlist__add');
    add.type = 'button';
    add.setAttribute('data-beyza-wishlist-add', '');
    var addLabel = el('span', '', strings.labelAdd || 'Add to cart');
    addLabel.setAttribute('data-beyza-add-label', '');
    add.appendChild(addLabel);
    if (item.variantId) add.dataset.variantId = item.variantId; else add.disabled = true;

    var remove = el('button', 'beyza-wishlist__remove', strings.labelRemove || 'Remove from wishlist');
    remove.type = 'button';
    remove.setAttribute('data-beyza-wishlist-remove', item.handle);

    info.append(title, price, note, add, remove);
    li.append(mediaWrap, info);

    // Refresh live product data (price, availability, options) from Shopify.
    fetchProduct(item).then(function (product) {
      if (!li.isConnected || !product) return;
      if (product.missing) {
        note.hidden = false;
        note.textContent = strings.labelUnavailable || 'No longer available';
        add.disabled = true;
        return;
      }
      if (product.title) titleLink.textContent = product.title;
      var img0 = product.featured_image || (product.images && product.images[0]);
      if (img0 && !item.image) {
        var src0 = img0.indexOf('//') === 0 ? 'https:' + img0 : img0;
        img.src = src0 + (src0.indexOf('?') > -1 ? '&' : '?') + 'width=600';
        img.hidden = false;
      }
      var variants = product.variants || [];
      var available = variants.filter(function (v) { return v.available; });
      var chosen = available[0] || variants[0];
      if (chosen) {
        current.textContent = money(chosen.price);
        var oldCompare = price.querySelector('.beyza-wishlist__price-compare');
        if (oldCompare) oldCompare.remove();
        current.classList.toggle('is-sale', chosen.compare_at_price > chosen.price);
        if (chosen.compare_at_price > chosen.price) price.appendChild(el('s', 'beyza-wishlist__price-compare', money(chosen.compare_at_price)));
      }
      var needsChoice = variants.length > 1;
      if (!available.length) {
        add.disabled = true;
        addLabel.textContent = strings.labelSoldOut || 'Sold out';
      } else if (needsChoice) {
        var choose = el('a', 'beyza-wishlist__add', strings.labelChoose || 'Choose options');
        choose.href = item.url;
        add.replaceWith(choose);
      } else {
        add.disabled = false;
        add.dataset.variantId = String(chosen.id);
      }
    });
    return li;
  }

  function renderPage(page) {
    var list = read();
    var grid = page.querySelector('[data-beyza-wishlist-grid]');
    var empty = page.querySelector('[data-beyza-wishlist-empty]');
    var countEl = page.querySelector('[data-beyza-wishlist-page-count]');
    if (!grid || !empty) return;
    page.classList.remove('is-loading');
    if (countEl) countEl.textContent = list.length ? '(' + list.length + ')' : '';
    empty.hidden = list.length > 0;
    grid.hidden = list.length === 0;

    var existing = {};
    Array.prototype.forEach.call(grid.children, function (li) { existing[li.dataset.handle] = li; });
    var frag = document.createDocumentFragment();
    list.forEach(function (item) {
      frag.appendChild(existing[item.handle] || buildItem(page, item));
      delete existing[item.handle];
    });
    grid.textContent = '';
    grid.appendChild(frag);
  }

  function renderPages() {
    document.querySelectorAll('[data-beyza-wishlist-page]').forEach(renderPage);
  }

  /* ---------------- Events ---------------- */
  document.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    var rm = target.closest('[data-beyza-wishlist-remove]');
    if (rm) {
      event.preventDefault();
      event.stopPropagation();
      var li = rm.closest('.beyza-wishlist__item');
      var handle = rm.getAttribute('data-beyza-wishlist-remove');
      if (li && !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
        li.classList.add('is-removing');
        setTimeout(function () { remove(handle); }, 260);
      } else {
        remove(handle);
      }
      return;
    }

    var addBtn = target.closest('[data-beyza-wishlist-add]');
    if (addBtn && addBtn.tagName === 'BUTTON') {
      event.preventDefault();
      event.stopPropagation();
      if (addBtn.disabled || !addBtn.dataset.variantId || addBtn.getAttribute('aria-busy') === 'true') return;
      var page = addBtn.closest('[data-beyza-wishlist-page]');
      var ds = page ? page.dataset : {};
      var label = addBtn.querySelector('[data-beyza-add-label]') || addBtn;
      var idle = label.textContent;
      addBtn.setAttribute('aria-busy', 'true');
      label.textContent = ds.labelAdding || 'Adding…';
      Promise.resolve(addToCart(addBtn.dataset.variantId, addBtn)).then(function () {
        label.textContent = ds.labelAdded || 'Added to cart';
        addBtn.classList.add('is-added');
      }).catch(function () {
        label.textContent = ds.labelError || 'Could not add';
      }).then(function () {
        addBtn.removeAttribute('aria-busy');
        setTimeout(function () { label.textContent = idle; addBtn.classList.remove('is-added'); }, 2200);
      });
    }
  });

  window.addEventListener('storage', function (e) {
    if (e.key === KEY || e.key === null) {
      paint(document);
      renderPages();
    }
  });

  // The product card script handles clicks on [data-beyza-wishlist]; this fallback only runs
  // when that script is not on the page (e.g. Beyza cards turned off in Theme settings).
  document.addEventListener('click', function (event) {
    if (window.__beyzaProductCard) return;
    var target = event.target instanceof Element ? event.target : null;
    var btn = target && target.closest('[data-beyza-wishlist]');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    var active = btn.getAttribute('aria-pressed') !== 'true';
    toggle({ handle: btn.dataset.productHandle, productId: btn.dataset.productId, active: active, button: btn });
  });

  window.BeyzaWishlist = {
    __beyza: true,
    storageAvailable: !!store,
    items: items,
    count: count,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    paint: paint,
    render: renderPages
  };

  function start() {
    paint(document);
    renderPages();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', start);
})();
